import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  AuthProvider as CoreAuthProvider,
  useEmailConfirmationReminder,
} from '@doger/core/modules/auth';
import { environment } from '@doger/core/shared/config';

import { useAnalytics } from '@doger/chat/shared/analytics';

import { useApplyForAnWhitelistReminder } from './apply-for-an-whitelist';
import { DEFAULT_AUTH_TYPE } from './auth-modal.component';

type AuthContextType = {
  isAuthModalOpen: boolean;
  setIsAuthModalOpen: (isAuthModalOpen: boolean) => void;
  authType: string | null;
  setAuthType: (authType: string) => void;
  isEmailConfirmationModalOpen: boolean;
  setIsEmailConfirmationModalOpen: (isOpen: boolean) => void;
  isUserProfileDetailsModalOpen: boolean;
  setIsUserProfileDetailsModalOpen: (isOpen: boolean) => void;
  isWhitelistModalOpen: boolean;
  setIsWhitelistModalOpen: (isOpen: boolean) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const analytics = useAnalytics();

  const [authType, setAuthType] = useSearchParamsState(
    'auth',
    z.enum(['signin', 'signup'])
  );

  const [isEmailConfirmationModalOpen, setIsEmailConfirmationModalOpen] =
    useState(false);

  const [isUserProfileDetailsModalOpen, setIsUserProfileDetailsModalOpen] =
    useState(false);

  const [isWhitelistModalOpen, setIsWhitelistModalOpen] = useState(false);

  // Email confirmation reminder hook
  useEmailConfirmationReminder({
    enabled: !isEmailConfirmationModalOpen,
    onConfirmEmail: () => setIsEmailConfirmationModalOpen(true),
  });

  useApplyForAnWhitelistReminder({
    enabled: !isWhitelistModalOpen,
    onShowWhitelistModal: () => setIsWhitelistModalOpen(true),
  });

  const onGoogleOneTapSuccess = useCallback(
    (data: {
      accessToken: string;
      refreshToken: string;
      isExistingUser: boolean;
    }) => {
      if (!data.isExistingUser) {
        analytics.amplitude.track({
          event: 'User Signed Up',
          properties: {
            source: 'google',
            googleAuthType: 'one-tap',
          },
        });
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      isAuthModalOpen: !!authType,
      setIsAuthModalOpen: (isAuthModalOpen: boolean) =>
        isAuthModalOpen ? setAuthType(DEFAULT_AUTH_TYPE) : setAuthType(null),
      authType,
      setAuthType,
      isEmailConfirmationModalOpen,
      setIsEmailConfirmationModalOpen,
      isUserProfileDetailsModalOpen,
      setIsUserProfileDetailsModalOpen,
      isWhitelistModalOpen,
      setIsWhitelistModalOpen,
    }),
    [
      authType,
      setAuthType,
      isEmailConfirmationModalOpen,
      isUserProfileDetailsModalOpen,
      isWhitelistModalOpen,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      <CoreAuthProvider
        signOutUrl='/'
        signInUrl='/'
        GOOGLE_CLIENT_ID={environment.google.clientId}
        disabledGoogleOneTapAuth={value.isAuthModalOpen}
        onGoogleOneTapSuccess={onGoogleOneTapSuccess}
      >
        {children}
      </CoreAuthProvider>
    </AuthContext.Provider>
  );
};

const useSearchParamsState = (name: string, schema: z.ZodType<string>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = searchParams.get(name);
  const result = schema.safeParse(value);

  const setValue = useCallback(
    (value: string | null) => {
      setSearchParams(
        (searchParams) => {
          if (value === null) {
            searchParams.delete(name);

            return searchParams;
          }

          const result = schema.safeParse(value);

          if (result.success) {
            searchParams.set(name, result.data);

            return searchParams;
          }

          searchParams.delete(name);

          return searchParams;
        },
        { replace: true }
      );
    },
    [name]
  );

  useEffect(() => {
    if (!result.success) {
      setValue(null);
    }
  }, [result.success]);

  return [result.success ? value : null, setValue] as const;
};
